/**
 * floorCheck — Single source of truth for "is this piece above the survival floor?"
 *
 * Definition: A piece is "above floor" if its asking price >= its calculated floor (TTCP).
 * The floor already includes materials + labor (hours × survival target × tier multiplier).
 * So this is a total-price comparison, not an hourly comparison.
 *
 * Why not hourly? Because the floor formula already accounts for hours. Comparing
 * gutPrice >= floor answers "does this price cover ALL my costs for this piece?"
 * which is the question artists actually care about.
 *
 * Used by: portfolioHealth, healthScore, StudioTab belowFloorCount, roast payload.
 */

export interface FloorCheckInput {
  /** Artist's asking price in cents. Falls back to truePrice if not set. */
  gutPrice: number | null;
  /** TruePrice-calculated price in cents (TTCP — always present on a saved verdict). */
  truePrice: number;
  /** TruePrice-calculated floor in cents (TTCP — always present on a saved verdict). */
  floor: number;
}

export function isAboveFloor(input: FloorCheckInput): boolean {
  const askingPrice = input.gutPrice ?? input.truePrice;
  return askingPrice >= input.floor;
}

/**
 * @deprecated Use computePortfolioHealth from portfolioHealth.ts instead.
 * Batch utility for health score calculations.
 * Returns { above, below, total, score } counts.
 */
export function countFloorHealth(
  verdicts: FloorCheckInput[]
): { above: number; below: number; total: number; score: number } {
  const total = verdicts.length;
  if (total === 0) return { above: 0, below: 0, total: 0, score: 0 };

  const above = verdicts.filter(v => isAboveFloor(v)).length;
  const below = total - above;
  const score = Math.round((above / total) * 100);

  return { above, below, total, score };
}
